import {
  Component, OnInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { take } from 'rxjs/operators';
import { NmiPosPaymentService } from '../../services/nmi-pos-payment.service';
import { PosDevice } from '../../../models/nmi.models';

@Component({
  selector: 'nmi-pos-devices',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './nmi-pos-devices.component.html',
  styleUrls: ['./nmi-pos-devices.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NmiPosDevicesComponent implements OnInit {
  devices: PosDevice[] = [];
  loading = false;

  registrationCode = '';
  deviceNickname = '';
  registering = false;
  registerError = '';

  editingDeviceId: string | null = null;
  editNickname = '';
  saving = false;

  confirmRemoveId: string | null = null;
  removing = false;

  constructor(
    private posService: NmiPosPaymentService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void { this.loadDevices(); }

  loadDevices(): void {
    this.loading = true;
    this.posService.getDevices().pipe(take(1)).subscribe({
      next: devices => { this.devices = devices.filter(d => d.registration_status !== 'deregistered'); this.loading = false; this.cdr.markForCheck(); },
      error: () => { this.loading = false; this.cdr.markForCheck(); },
    });
  }

  registerDevice(): void {
    if (!this.registrationCode.trim() || !this.deviceNickname.trim()) return;
    this.registering = true;
    this.registerError = '';
    this.posService.registerDevice({
      registration_code: this.registrationCode.trim(),
      device_nickname: this.deviceNickname.trim(),
    }).pipe(take(1)).subscribe({
      next: () => { this.registrationCode = ''; this.deviceNickname = ''; this.registering = false; this.loadDevices(); },
      error: (err: any) => {
        this.registerError = err?.error?.error ?? 'Registration failed. Check the code on the terminal.';
        this.registering = false; this.cdr.markForCheck();
      },
    });
  }

  startEdit(device: PosDevice): void {
    this.editingDeviceId = device.poi_device_id;
    this.editNickname = device.device_nickname;
    this.cdr.markForCheck();
  }

  cancelEdit(): void { this.editingDeviceId = null; this.editNickname = ''; this.cdr.markForCheck(); }

  saveEdit(poiDeviceId: string): void {
    if (!this.editNickname.trim()) return;
    this.saving = true;
    this.posService.updateDevice({
      poi_device_id: poiDeviceId, device_nickname: this.editNickname.trim(),
    }).pipe(take(1)).subscribe({
      next: updated => {
        const idx = this.devices.findIndex(d => d.poi_device_id === poiDeviceId);
        if (idx !== -1) this.devices = [...this.devices.slice(0, idx), updated, ...this.devices.slice(idx + 1)];
        this.editingDeviceId = null; this.saving = false; this.cdr.markForCheck();
      },
      error: () => { this.saving = false; this.cdr.markForCheck(); },
    });
  }

  promptRemove(poiDeviceId: string): void { this.confirmRemoveId = poiDeviceId; this.cdr.markForCheck(); }
  cancelRemove(): void { this.confirmRemoveId = null; this.cdr.markForCheck(); }

  confirmRemove(poiDeviceId: string): void {
    this.removing = true;
    this.posService.deregisterDevice(poiDeviceId).pipe(take(1)).subscribe({
      next: () => {
        this.devices = this.devices.filter(d => d.poi_device_id !== poiDeviceId);
        this.confirmRemoveId = null; this.removing = false; this.cdr.markForCheck();
      },
      error: () => { this.removing = false; this.cdr.markForCheck(); },
    });
  }

  trackByDeviceId(_i: number, d: PosDevice): string { return d.poi_device_id; }
}
